import {
    initializeFirestore,
    getFirestore,
    connectFirestoreEmulator,
    persistentLocalCache,
    persistentMultipleTabManager,
    memoryLocalCache,
    terminate,
    clearIndexedDbPersistence,
    CACHE_SIZE_UNLIMITED,
} from 'firebase/firestore';
import { isImpersonationTab } from './auth.js';

function isWebKit() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    // Safari + iOS browsers carry "Safari" without "Chrome"/"Chromium" (CriOS/FxiOS qualify).
    const isSafariFamily = /Safari/.test(ua) && !/Chrom(e|ium)/.test(ua);
    // iPadOS 13+ reports a desktop "Macintosh" UA but is still WebKit + multi-touch.
    const isIpadOsDesktop =
        /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
    return isSafariFamily || isIpadOsDesktop;
}

/**
 * Pick the local cache strategy.
 *
 * - Impersonation tabs stay memory-only so impersonated-user documents are
 *   never written to the shared IndexedDB (mirrors the session-only auth
 *   persistence in {@link createAuth}).
 */
function buildLocalCache() {
    const hasIndexedDb = typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
    if (!hasIndexedDb || isImpersonationTab()) {
        return memoryLocalCache();
    }
    return persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
        cacheSizeBytes: CACHE_SIZE_UNLIMITED,
    });
}

/**
 * Create a Firestore instance, optionally targeting a named database.
 *
 * @param {import('firebase/app').FirebaseApp} firebase
 * @param {object} [opts]
 * @param {string} [opts.databaseId]
 *   Omit or pass '(default)' for the default database.
 * @returns {import('firebase/firestore').Firestore}
 */
export function createFirestore(firebase, { databaseId } = {}) {
    const named = databaseId && databaseId !== '(default)';
    // Force long polling for WebKit
    const forceLongPolling = import.meta.env.VITE_FIRESTORE_FORCE_LONG_POLLING === 'true' || isWebKit();
    const settings = {
        ...(forceLongPolling
            ? { experimentalForceLongPolling: true }
            : { experimentalAutoDetectLongPolling: true }),
        localCache: buildLocalCache(),
    };

    let db;
    try {
        db = named
            ? initializeFirestore(firebase, settings, databaseId)
            : initializeFirestore(firebase, settings);
    } catch {
        // Already initialized for this database — reuse the existing instance.
        db = named ? getFirestore(firebase, databaseId) : getFirestore(firebase);
    }

    if (import.meta.env.DEV) {
        const host = import.meta.env.VITE_EMULATOR_HOST || 'localhost';
        const port = Number(import.meta.env.VITE_EMULATOR_PORT_FIRESTORE) || 8080;
        connectFirestoreEmulator(db, host, port);
        // Firestore SDK hardcodes ssl: false in connectFirestoreEmulator,
        // so flip it back on when the page is served over HTTPS.
        if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
            db._setSettings({ ...db._getSettings(), ssl: true });
        }
    }

    return db;
}

/**
 * Tear down a Firestore instance and wipe its persisted IndexedDB cache.
 *
 * @param {import('firebase/firestore').Firestore} db
 * @returns {Promise<void>}
 */
export async function clearFirestoreCache(db) {
    if (!db) return;
    try {
        await terminate(db);
    } catch (err) {
        console.error('[firestore] terminate failed', err);
    }
    try {
        await clearIndexedDbPersistence(db);
    } catch (err) {
        // Throws if another tab still holds the DB open or the cache is
        // memory-only — neither is fatal for sign-out.
        console.debug('[firestore] clearIndexedDbPersistence skipped:', err?.message);
    }
}
